import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Circle } from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion'
import { useTimerStore } from '../../stores/timer-store'
import { cn } from '../../lib/utils'

export function TimerSetList() {
  const { t } = useTranslation()
  const templates = useTimerStore((s) => s.templates)
  const templateIdx = useTimerStore((s) => s.templateIdx)
  const setIdx = useTimerStore((s) => s.setIdx)
  const repIdx = useTimerStore((s) => s.repIdx)
  const phase = useTimerStore((s) => s.phase)
  const completedReps = useTimerStore((s) => s.completedReps)

  const tpl = templates[templateIdx]
  const [openValue, setOpenValue] = useState<string>(`set-${setIdx}`)

  // Auto-open the current set whenever it changes
  useEffect(() => {
    setOpenValue(`set-${setIdx}`)
  }, [setIdx])

  if (!tpl) return null
  const sets = Array.from({ length: tpl.sets }, (_, i) => i)

  return (
    <Accordion
      type="single"
      collapsible
      value={openValue}
      onValueChange={(v) => setOpenValue(v)}
      className="w-full"
    >
      {sets.map((sIdx) => {
        const isPast = phase === 'completed' || sIdx < setIdx
        const isCurrent = sIdx === setIdx && phase !== 'completed'
        return (
          <AccordionItem
            key={sIdx}
            value={`set-${sIdx}`}
            className={cn('border-b last:border-b-0', isCurrent && 'bg-accent/30')}
          >
            <AccordionTrigger
              className={cn(
                'py-2 hover:no-underline text-sm',
                isPast && 'text-muted-foreground',
              )}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {isPast ? (
                  <Check className="size-4 text-green-500 shrink-0" />
                ) : isCurrent ? (
                  <span
                    aria-hidden
                    className="size-2 rounded-full bg-primary shrink-0 ml-1"
                  />
                ) : (
                  <Circle className="size-4 text-muted-foreground/40 shrink-0" />
                )}
                <span className="flex-1 text-left">
                  {t('workoutTimer.setList.setLabel', 'Set {{n}}', { n: sIdx + 1 })}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {tpl.manual && tpl.reps_target != null
                    ? t('workoutTimer.setList.repsSummary', '{{reps}} reps', {
                        reps: tpl.reps_target * tpl.reps_per_set,
                      })
                    : tpl.work_seconds != null
                      ? t('workoutTimer.setList.timedSummary', '{{work}}s × {{reps}}', {
                          work: tpl.work_seconds,
                          reps: tpl.reps_per_set,
                        })
                      : ''}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-2 pt-0">
              <RepList
                templateIdx={templateIdx}
                setIdx={sIdx}
                currentSetIdx={setIdx}
                currentRepIdx={repIdx}
                currentPhase={phase}
                repsPerSet={tpl.reps_per_set}
                workSeconds={tpl.work_seconds}
                manual={tpl.manual}
                repsTarget={tpl.reps_target}
                completedReps={completedReps}
              />
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}

interface RepListProps {
  templateIdx: number
  setIdx: number
  currentSetIdx: number
  currentRepIdx: number
  currentPhase: string
  repsPerSet: number
  workSeconds: number | null
  manual: boolean
  repsTarget: number | null
  completedReps: string[]
}

function RepList(props: RepListProps) {
  const { t } = useTranslation()
  const reps = Array.from({ length: props.repsPerSet }, (_, i) => i)

  return (
    <ul className="flex flex-col gap-1 pl-6">
      {reps.map((rIdx) => {
        const key = `${props.templateIdx}-${props.setIdx}-${rIdx}`
        const done = props.completedReps.includes(key)
        const isCurrentSet = props.setIdx === props.currentSetIdx
        const isCurrentRep = isCurrentSet && rIdx === props.currentRepIdx && props.currentPhase !== 'completed'

        const dotClass = done
          ? 'bg-green-500'
          : isCurrentRep
            ? 'bg-orange-500 ring-2 ring-orange-500/30'
            : 'bg-muted-foreground/30'

        const textClass = done
          ? 'text-green-600 dark:text-green-400'
          : isCurrentRep
            ? 'text-foreground font-medium'
            : 'text-muted-foreground'

        const summary = props.manual && props.repsTarget != null
          ? t('workoutTimer.setList.repSummaryManual', '{{count}} reps', { count: props.repsTarget })
          : props.workSeconds != null
            ? t('workoutTimer.setList.repSummaryTimed', '{{work}}s', { work: props.workSeconds })
            : ''

        return (
          <li
            key={rIdx}
            className={cn('flex items-center gap-2 text-xs py-0.5', textClass)}
            data-testid={`rep-row-${props.setIdx}-${rIdx}`}
          >
            <span aria-hidden className={cn('size-2 rounded-full shrink-0 transition-colors', dotClass)} />
            <span className="flex-1">
              {t('workoutTimer.setList.repLabel', 'Rep {{n}}', { n: rIdx + 1 })}
            </span>
            <span className="tabular-nums">{summary}</span>
          </li>
        )
      })}
    </ul>
  )
}
